import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Field, Input, Textarea, Badge, Spinner, EmptyState } from "@/components/ui";
import { useAssignmentTypes, useSaveTranslation, translationFor } from "@/lib/queries";

export const Route = createFileRoute("/app/assignment-types")({
  component: TypesSettingsPage,
});

function TypesSettingsPage() {
  const { data: types, isLoading } = useAssignmentTypes();
  const save = useSaveTranslation();
  const [adding, setAdding] = useState<string | null>(null); // type id being added a language for

  if (isLoading) return <Spinner label={t("app.loading")} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t("typesPage.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("typesPage.subtitle")}</p>
      </div>

      {(types ?? []).length === 0 ? (
        <EmptyState>{t("repo.empty")}</EmptyState>
      ) : (
        (types ?? []).map((type) => {
          const en = translationFor(type, "en");
          const langs = type.translations.map((x) => x.languageCode);
          return (
            <Card key={type.id} className="space-y-4 p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{en?.name ?? type.code}</span>
                <Badge>{type.code}</Badge>
                <Badge tone="muted">{type.baseContentKind}</Badge>
                <div className="flex gap-1">
                  {langs.map((l) => (
                    <Badge key={l} tone="blue">
                      {l.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {type.translations.map((tr) => (
                  <TranslationForm
                    key={tr.id}
                    typeId={type.id}
                    initial={tr}
                    onSave={(v) => save.mutateAsync({ typeId: type.id, ...v })}
                    saving={save.isPending}
                  />
                ))}
                {adding === type.id && (
                  <TranslationForm
                    typeId={type.id}
                    onSave={(v) => save.mutateAsync({ typeId: type.id, ...v })}
                    saving={save.isPending}
                  />
                )}
              </div>

              <Button
                variant="ghost"
                onClick={() => setAdding(adding === type.id ? null : type.id)}
              >
                {adding === type.id ? t("common.cancel") : `＋ ${t("typesPage.addLanguage")}`}
              </Button>
            </Card>
          );
        })
      )}
    </div>
  );
}

type Tr = { languageCode: string; name: string; description: string; classroomLabel: string };

function TranslationForm({
  typeId,
  initial,
  onSave,
  saving,
}: {
  typeId: string;
  initial?: Tr;
  onSave: (v: Tr) => Promise<unknown>;
  saving: boolean;
}) {
  const [code, setCode] = useState(initial?.languageCode ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [label, setLabel] = useState(initial?.classroomLabel ?? "");
  const [done, setDone] = useState(false);
  const ready = /^[a-z]{2}(-[A-Za-z]{2,4})?$/.test(code) && name.trim() && label.trim();

  // When "start adding" was clicked, initial is undefined → editable code field.
  return (
    <div className="space-y-3 rounded-lg border p-4">
      {initial ? (
        <Badge tone="blue">{code.toUpperCase()}</Badge>
      ) : (
        <Input value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} placeholder="es" className="w-24" maxLength={7} />
      )}
      <Field label={t("typesPage.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dictado" />
      </Field>
      <Field label={t("typesPage.description")}>
        <Textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="🎧 Escucho. ✍️ Escribo."
        />
      </Field>
      <Field label={t("typesPage.classroomLabel")}>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Tarea" />
      </Field>
      <div className="flex items-center gap-2">
        <Button
          disabled={saving || !ready}
          onClick={async () => {
            await onSave({ languageCode: code, name: name.trim(), description, classroomLabel: label.trim() });
            setDone(true);
            setTimeout(() => setDone(false), 2000);
          }}
        >
          {t("common.save")}
        </Button>
        {done && <span className="text-sm text-green-600">✓ {t("edit.saved")}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{t("typesPage.classroomLabelHint", { label: label || "…" })}</p>
      <span className="hidden">{typeId}</span>
    </div>
  );
}